import React, { useEffect } from 'react';
import type { Match } from '@atlassian/jira-router';
import { APP_NAMES } from '@atlassian/jira-servicedesk-common/src/utils/app-names';
import { useArticleId } from '@atlassian/jira-servicedesk-knowledge-common/src/controllers/store/index.tsx';
import KnowledgeBaseBreadcrumb from '@atlassian/jira-servicedesk-knowledge-hub/src/view/common/knowledge-base-breadcrumb';
import KnowledgeBaseSearchBreadcrumb from '@atlassian/jira-servicedesk-knowledge-hub/src/view/common/knowledge-base-search-breadcrumb';
import KnowledgeHubViewArticle from '@atlassian/jira-servicedesk-knowledge-hub/src/view/knowledge-hub-article';
import CategoryBreadcrumbItem from '@atlassian/jira-servicedesk-knowledge-hub/src/view/knowledge-hub-category/category-breadcrumb-item';
import { toCategoryGuid } from '@atlassian/jira-servicedesk-knowledge-services/src/types';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import { KnowledgeHubAppBase } from '@atlassian/jira-spa-apps-knowledge-hub-app-base/src/index.tsx';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import UFOLabel from '@atlassian/jira-ufo-label';

const renderKnowledgeHubPageWithCorrectBreadcrumbs = (
	match: Match,
	accountId: AccountId | null,
	spaceKey?: string,
) => {
	if (match && match.params && match.params.categoryId) {
		return (
			<UFOLabel name="knowledge-hub-category-view-article">
				<CategoryBreadcrumbItem id={toCategoryGuid(match.params.categoryId || '')}>
					<KnowledgeHubViewArticle
						id={match.params.articleId}
						accountId={accountId}
						spaceKey={spaceKey}
					/>
				</CategoryBreadcrumbItem>
			</UFOLabel>
		);
	}
	if (match && match.url && match.url.includes('search')) {
		return (
			<UFOLabel name="knowledge-hub-search-view-article">
				<KnowledgeBaseSearchBreadcrumb>
					<KnowledgeHubViewArticle
						id={match && match.params && match.params.articleId}
						accountId={accountId}
						spaceKey={spaceKey}
					/>
				</KnowledgeBaseSearchBreadcrumb>
			</UFOLabel>
		);
	}

	return (
		<UFOLabel name="knowledge-hub-browse-view-article">
			<KnowledgeHubViewArticle
				id={match && match.params && match.params.articleId}
				accountId={accountId}
				spaceKey={spaceKey}
			/>
		</UFOLabel>
	);
};

const View = () => {
	const [, { resetDraftArticleData }] = useArticleId();
	const accountId = useAccountId();

	useEffect(
		// We don't want to reset deleted because this flag should show up in browse-all-articles component
		() => () => {
			resetDraftArticleData(false);
		},
		[resetDraftArticleData],
	);
	return (
		<>
			<MarkProductStart />
			<KnowledgeHubAppBase appName={APP_NAMES.KB_VIEW_ARTICLE}>
				{({ match, query }) => {
					const spaceKey = query ? query.spaceKey || query.spacekey : undefined;
					return (
						<KnowledgeBaseBreadcrumb>
							{renderKnowledgeHubPageWithCorrectBreadcrumbs(match, accountId, spaceKey)}
						</KnowledgeBaseBreadcrumb>
					);
				}}
			</KnowledgeHubAppBase>
		</>
	);
};

export default View;
